/**
 * PropertyList – main property list page
 * Mirrors Angular property-list: filters, table/grid, pagination, listed toggle
 */
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, useId } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useUserRole } from '@/hooks/useUserRole'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { PropertyTable } from '../components/PropertyTable'
import { PropertyGrid } from '../components/PropertyGrid'
import { PropertyFilters } from '../components/PropertyFilters'
import { usePropertyList } from '../hooks/usePropertyList'
import { buildPropertyExportUrl, updatePropertyListed, searchProperties } from '../services/propertyService'
import { PER_PAGE_OPTIONS, PROPERTY_SEARCH_DEBOUNCE_MS } from '../constants'
import { shouldShowPropertyListExport } from '../utils/propertyListLogic'
import type { Property } from '../types/property'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { showSuccess, showApiError } from '@/lib/utils/notifications'

export function PropertyList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasPolicy, hasWriteAccess } = useUserRole()
  const canAdd = hasPolicy(['property_add'])
  /** Angular: button uses property_listed_edit; updatePropertyListed also requires accessibleMenu.property Write. */
  const canToggleListed =
    hasPolicy(['property_listed_edit']) && hasWriteAccess('property')

  const {
    properties,
    loading,
    error,
    pagination,
    filters,
    activeFiltersCount,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
  } = usePropertyList()

  const isMobile = useIsMobile()
  const [isListView, setIsListView] = useState(true)

  // Mobile → grid view; Desktop → list view
  useEffect(() => {
    setIsListView(!isMobile)
  }, [isMobile])

  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [listedConfirm, setListedConfirm] = useState<{
    isOpen: boolean
    property: Property | null
    isCurrentlyActive: boolean
  }>({ isOpen: false, property: null, isCurrentlyActive: false })
  const listedToggleInFlightRef = useRef(false)
  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestionsAbortRef = useRef<AbortController | null>(null)
  const searchWrapperRef = useRef<HTMLDivElement>(null)
  const appliedCompanyIdFromUrl = useRef(false)
  /** Latest applied list search (from hook) — avoids stale closures and unstable effect deps. */
  const appliedSearchRef = useRef<string | undefined>(filters.search)
  const applyFiltersRef = useRef(applyFilters)
  const searchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const suggestionsDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // eslint-disable-next-line react-hooks/refs -- latest-value ref: written in render to avoid stale closures / unstable effect deps; read only in effects & handlers
  appliedSearchRef.current = filters.search
  // eslint-disable-next-line react-hooks/refs -- latest-value ref: written in render to avoid stale closures / unstable effect deps; read only in effects & handlers
  applyFiltersRef.current = applyFilters

  // Apply company_id from URL when coming from company edit (e.g. /property?company_id=123)
  useEffect(() => {
    const companyId = searchParams.get('company_id')
    if (companyId && !appliedCompanyIdFromUrl.current) {
      appliedCompanyIdFromUrl.current = true
      applyFilters({
        company_id: companyId,
        company_name: undefined,
        page: 1,
      })
      const next = new URLSearchParams(searchParams)
      next.delete('company_id')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams, applyFilters])

  useEffect(() => {
    setIsListView(!isMobile)
  }, [isMobile])

  /**
   * Keep the input aligned with applied filters in the same commit *before* paint.
   * If this runs in useEffect, the debounce effect can still see the previous searchTerm
   * (e.g. after Reset filters) and wrongly re-apply the old search 500ms later.
   *
   * Compare trimmed values so applying a trimmed filter does not wipe a trailing/leading
   * space the user just typed (which would jump the cursor).
   */
  useLayoutEffect(() => {
    const next = filters.search || ''
    setSearchTerm((prev) => (prev.trim() === next.trim() ? prev : next))
  }, [filters.search])

  /** Debounce list fetch on typing; refs keep timer stable when hook updates. */
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current)
      searchDebounceTimerRef.current = null
    }
    searchDebounceTimerRef.current = setTimeout(() => {
      searchDebounceTimerRef.current = null
      const applied = String(appliedSearchRef.current ?? '').trim()
      const next = searchTerm.trim()
      if (next !== applied) {
        applyFiltersRef.current({ search: next || undefined })
      }
    }, PROPERTY_SEARCH_DEBOUNCE_MS)
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current)
        searchDebounceTimerRef.current = null
      }
    }
  }, [searchTerm])

  /** Apply search immediately and cancel pending debounce (Enter). */
  const flushSearchToFilters = useCallback(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current)
      searchDebounceTimerRef.current = null
    }
    if (suggestionsDebounceTimerRef.current) {
      clearTimeout(suggestionsDebounceTimerRef.current)
      suggestionsDebounceTimerRef.current = null
    }
    const applied = String(appliedSearchRef.current ?? '').trim()
    const next = searchTerm.trim()
    if (next !== applied) {
      applyFiltersRef.current({ search: next || undefined })
    }
  }, [searchTerm])

  /**
   * Angular `searchProperty`: call GET /api/all_property when text length > 0 and even
   * (`length % 2 === 0`). Cancels previous in-flight request (`unSubscribeOldRequest`).
   */
  const fetchSuggestions = useCallback(
    async (text: string) => {
      if (suggestionsAbortRef.current) {
        suggestionsAbortRef.current.abort()
        suggestionsAbortRef.current = null
      }
      if (!text.length) {
        setSuggestions([])
        return
      }
      if (text.length % 2 !== 0) return
      const abort = new AbortController()
      suggestionsAbortRef.current = abort
      try {
        const results = await searchProperties(
          text,
          filters.company_id,
          filters.listed || 'all',
          abort.signal,
        )
        if (abort.signal.aborted) return
        setSuggestions(results.map((p) => p.property_name))
        setShowSuggestions(true)
      } catch {
        /* aborted or network error */
      }
    },
    [filters.company_id, filters.listed]
  )

  const fetchSuggestionsRef = useRef(fetchSuggestions)
  // eslint-disable-next-line react-hooks/refs -- latest-value ref: written in render to avoid stale closures / unstable effect deps; read only in effects & handlers
  fetchSuggestionsRef.current = fetchSuggestions

  /**
   * Debounce GET /api/all_property (autocomplete) like GET /api/property (list).
   * Without this, all_property ran on every onChange keystroke.
   */
  useEffect(() => {
    if (suggestionsDebounceTimerRef.current) {
      clearTimeout(suggestionsDebounceTimerRef.current)
      suggestionsDebounceTimerRef.current = null
    }
    const trimmed = searchTerm.trim()
    if (!trimmed) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    suggestionsDebounceTimerRef.current = setTimeout(() => {
      suggestionsDebounceTimerRef.current = null
      void fetchSuggestionsRef.current(trimmed)
    }, PROPERTY_SEARCH_DEBOUNCE_MS)
    return () => {
      if (suggestionsDebounceTimerRef.current) {
        clearTimeout(suggestionsDebounceTimerRef.current)
        suggestionsDebounceTimerRef.current = null
      }
    }
  }, [searchTerm])

  const handleSearchInput = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const selectSuggestion = useCallback(
    (name: string) => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current)
        searchDebounceTimerRef.current = null
      }
      if (suggestionsDebounceTimerRef.current) {
        clearTimeout(suggestionsDebounceTimerRef.current)
        suggestionsDebounceTimerRef.current = null
      }
      setSearchTerm(name)
      setSuggestions([])
      setShowSuggestions(false)
      applyFilters({ search: name })
    },
    [applyFilters]
  )

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleAddProperty = () => {
    navigate('/property/add')
  }

  const handleToggleListed = (property: Property) => {
    // Angular updatePropertyListed: requires accessibleMenu.property Write (+ button policy).
    if (!canToggleListed) return
    setListedConfirm({
      isOpen: true,
      property,
      isCurrentlyActive: property.org_listed_ids?.['1'] === 1,
    })
  }

  const handleConfirmToggleListed = async () => {
    if (listedToggleInFlightRef.current) return
    const { property, isCurrentlyActive } = listedConfirm
    if (!property?.property_id || property.company_id == null || !canToggleListed) {
      setListedConfirm((p) => ({ ...p, isOpen: false }))
      return
    }
    // Angular: `org_id: property.org_id` from row data (null/omitted when not set — do not default to Liberty org 1).
    listedToggleInFlightRef.current = true
    try {
      await updatePropertyListed(
        property.property_id,
        isCurrentlyActive ? 0 : 1,
        Number(property.company_id),
        property.org_id
      )
      showSuccess('Property Updated  Successfully!')
      setListedConfirm((p) => ({ ...p, isOpen: false }))
      // Angular triggerFilter resets to page 1 after listed toggle.
      applyFilters({ page: 1 })
    } catch (err) {
      showApiError(err)
      setListedConfirm((p) => ({ ...p, isOpen: false }))
    } finally {
      listedToggleInFlightRef.current = false
    }
  }

  const totalPages = pagination.last || 1
  const currentPage = pagination.current || 1

  const propertyExportUrl = useMemo(
    () => buildPropertyExportUrl(filters),
    [filters]
  )
  const perPageSelectId = useId()

  /** Narrow: column; sm+: page buttons and page/per-page on one row (scroll if needed). */
  const PaginationBar = ({
    className = '',
    perPageId,
    showPerPage = true,
  }: {
    className?: string
    perPageId: string
    /** Top bar omits per-page; bottom bar keeps the control. */
    showPerPage?: boolean
  }) => (
    <div
      className={`flex w-full min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-end sm:gap-x-2 sm:gap-y-0 ${className}`}
    >
        <div className="flex w-full min-w-0 min-h-0 flex-1 flex-nowrap items-center justify-center gap-1 overflow-x-auto sm:min-w-0 sm:justify-end sm:gap-1.5">
          <button
            data-testid="property-list-page-first-button"
            className="flex h-8 min-w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-1.5 text-xs text-slate-500 hover:bg-slate-50 disabled:opacity-50 sm:min-w-[32px] sm:px-2 sm:text-sm"
            onClick={() => changePage(1)}
            disabled={currentPage === 1}
            type="button"
            aria-label="First page"
          >
            &laquo;
          </button>
          <button
            data-testid="property-list-page-prev-button"
            className="flex h-8 min-w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-1.5 text-xs text-slate-500 hover:bg-slate-50 disabled:opacity-50 sm:min-w-[32px] sm:px-2 sm:text-sm"
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 1}
            type="button"
            aria-label="Previous page"
          >
            &lsaquo;
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number
            if (totalPages <= 5) pageNum = i + 1
            else if (currentPage <= 3) pageNum = i + 1
            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i
            else pageNum = currentPage - 2 + i
            const isActive = currentPage === pageNum
            return (
              <button
                key={pageNum}
                data-testid={`property-list-page-${pageNum}-button`}
                className={`flex h-8 min-w-8 shrink-0 items-center justify-center rounded-lg border px-1.5 text-xs font-medium sm:min-w-[32px] sm:px-2 sm:text-sm ${
                  isActive
                    ? 'border-[rgb(21_151_255)] bg-[rgb(21_151_255)] text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => changePage(pageNum)}
                type="button"
              >
                {pageNum}
              </button>
            )
          })}
          <button
            data-testid="property-list-page-next-button"
            className="flex h-8 min-w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-1.5 text-xs text-slate-500 hover:bg-slate-50 disabled:opacity-50 sm:min-w-[32px] sm:px-2 sm:text-sm"
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages}
            type="button"
            aria-label="Next page"
          >
            &rsaquo;
          </button>
          <button
            data-testid="property-list-page-last-button"
            className="flex h-8 min-w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-1.5 text-xs text-slate-500 hover:bg-slate-50 disabled:opacity-50 sm:min-w-[32px] sm:px-2 sm:text-sm"
            onClick={() => changePage(totalPages)}
            disabled={currentPage === totalPages}
            type="button"
            aria-label="Last page"
          >
            &raquo;
          </button>
        </div>

        <div className="flex w-full min-w-0 shrink-0 flex-col items-center justify-center gap-1.5 sm:w-auto sm:flex-row sm:flex-nowrap sm:items-center sm:justify-end sm:gap-2">
          {totalPages > 1 && (
            <div className="flex w-full min-w-0 max-w-sm flex-nowrap items-center justify-center gap-1.5 sm:w-auto sm:max-w-none sm:justify-end">
              <span className="shrink-0 text-xs text-slate-600 sm:text-sm">Page</span>
              <input
                data-testid="property-list-page-input"
                type="number"
                min={1}
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10)
                  if (!Number.isNaN(v) && v >= 1 && v <= totalPages) changePage(v)
                }}
                className="w-14 min-w-0 shrink rounded border border-slate-200 px-2 py-1.5 text-center text-xs sm:w-16 sm:py-1 sm:text-sm"
                aria-label="Page number"
              />
              <span className="shrink-0 text-xs text-slate-600 sm:text-sm">of {totalPages}</span>
            </div>
          )}

          {showPerPage ? (
            <div className="flex shrink-0 items-center justify-center gap-1.5 sm:gap-2 sm:pl-0">
              <label
                htmlFor={perPageId}
                className="shrink-0 text-xs font-semibold whitespace-nowrap text-slate-600 sm:text-sm"
              >
                Per page
              </label>
              <select
                data-testid="property-list-per-page-select"
                id={perPageId}
                className="min-w-0 max-w-full rounded-lg border border-slate-200 bg-white p-1.5 text-xs text-slate-700 sm:p-1.5 sm:text-sm"
                value={filters.perPage || 15}
                onChange={(e) => changePerPage(parseInt(e.target.value, 10))}
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
    </div>
  )

  return (
    <div data-testid="property-list-page" className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-12">
      <PageHeader title="PROPERTIES" testIdRoot="property-list-page-header" />

      <div
        data-testid="property-list-main"
        className="mx-auto flex w-full min-w-0 flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-6 sm:py-8"
      >
        <div
          data-testid="property-list-toolbar"
          className="flex w-full min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between"
        >
          <div
            ref={searchWrapperRef}
            data-testid="property-list-search-wrap"
            className="relative w-full min-w-0 max-w-lg md:w-1/2"
          >
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <input
              data-testid="property-list-search-input"
              type="text"
              placeholder="Search by Property Name or Zip Code"
              className="w-full pl-12 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 focus:border-[rgb(21_151_255)] placeholder:text-slate-400"
              value={searchTerm}
              onChange={(e) => handleSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  setShowSuggestions(false)
                  flushSearchToFilters()
                }
              }}
              onFocus={() => {
                if (suggestions.length > 0) setShowSuggestions(true)
              }}
              autoComplete="off"
            />
            {showSuggestions && suggestions.length > 0 && (
              <ul data-testid="property-list-suggestions" className="absolute left-0 right-0 top-full mt-1 z-50 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                {suggestions.map((name, i) => (
                  <li
                    key={`${name}-${i}`}
                    data-testid={`property-list-suggestion-${i}`}
                    className="px-4 py-2.5 text-sm text-slate-800 cursor-pointer hover:bg-slate-100 truncate"
                    onMouseDown={() => selectSuggestion(name)}
                  >
                    {name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div
            data-testid="property-list-toolbar-actions"
            className="flex w-full items-center gap-2 sm:gap-3 md:w-auto"
          >
            <button
              data-testid="property-list-filters-button"
              onClick={() => setIsFilterOpen(true)}
              className={`relative flex-1 md:flex-none flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all text-sm whitespace-nowrap ${
                activeFiltersCount > 0 ? 'border-[rgb(21_151_255)] text-[rgb(21_151_255)]' : ''
              }`}
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
                aria-hidden
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
              {activeFiltersCount > 0 && (
                <span
                  data-testid="property-list-filters-active-count-badge"
                  className="absolute -top-2 -right-2 bg-[rgb(21_151_255)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm"
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>
            {canAdd && (
              <button
                data-testid="property-list-add-button"
                onClick={handleAddProperty}
                className={cn(primaryEntityActionButtonClass, 'flex-[2] md:flex-none gap-2')}
                type="button"
              >
                <Plus className="h-5 w-5" />
                Add Property
              </button>
            )}
          </div>
        </div>

        <PropertyFilters
          filters={filters}
          onFiltersChange={applyFilters}
          onReset={resetFilters}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        />

        <div
          data-testid="property-list-results-container"
          className="relative flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
        >
          {error && (
            <div
              data-testid="property-list-error"
              className="m-4 rounded-lg bg-red-50 p-4 text-center text-red-600 sm:p-8"
            >
              {error}
            </div>
          )}

          {!error && (
            <>
              <div
                  data-testid="property-list-pagination-header-bar"
                  className={cn(
                    'flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 px-3 py-2.5 sm:gap-3 sm:px-6 sm:py-3 lg:flex-row lg:items-center lg:justify-between',
                    loading && 'pointer-events-none',
                  )}
                >
                  <div
                    data-testid="property-list-pagination-header-summary"
                    className="shrink-0 text-center text-xs font-medium text-slate-500 sm:text-sm lg:max-w-md lg:text-left"
                  >
                    Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
                    <span className="font-bold text-slate-900">{totalPages}</span>
                    <span className="mx-2 text-slate-300">|</span>
                    Total <span className="font-bold text-slate-900">{pagination.total}</span> Records
                  </div>
                  <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-between sm:gap-x-2 sm:gap-y-0 lg:flex-1">
                    <PaginationBar
                      perPageId={perPageSelectId}
                      showPerPage={false}
                      className="min-w-0 w-full sm:min-w-0 sm:flex-1 sm:basis-0"
                    />
                    {shouldShowPropertyListExport(properties.length) && (
                      <a
                        data-testid="property-list-export-link"
                        href={propertyExportUrl}
                        className={cn(
                          primaryEntityActionButtonClass,
                          'ml-auto h-8 min-w-0 w-auto shrink-0 px-3 text-xs sm:ml-0 sm:text-sm',
                        )}
                      >
                        Export
                      </a>
                    )}
                  </div>
                </div>

              <div
                data-testid="property-list-data-scroll"
                className="min-w-0"
              >
                <div data-testid="property-list-data-container" className="min-w-full">
                  {isListView ? (
                    <PropertyTable
                      properties={properties}
                      onSort={sort}
                      sortBy={filters.sortBy}
                      orderBy={filters.orderBy}
                      onToggleListed={handleToggleListed}
                      loading={loading}
                    />
                  ) : (
                    <PropertyGrid
                      properties={properties}
                      onToggleListed={handleToggleListed}
                      loading={loading}
                    />
                  )}
                </div>
              </div>

              <div
                  data-testid="property-list-pagination-footer-bar"
                  className={cn(
                    'flex flex-col gap-2 border-t border-slate-100 bg-slate-50/50 px-3 py-2.5 sm:gap-3 sm:px-6 sm:py-3 lg:flex-row lg:items-center lg:justify-between',
                    loading && 'pointer-events-none',
                  )}
                >
                  <div
                    data-testid="property-list-pagination-footer-summary"
                    className="shrink-0 text-center text-xs font-medium text-slate-500 sm:text-left sm:text-sm lg:max-w-md"
                  >
                    Page <span className="font-bold text-slate-900">{currentPage}</span> of{' '}
                    <span className="font-bold text-slate-900">{totalPages}</span>
                    <span className="mx-2 text-slate-300">|</span>
                    Total <span className="font-bold text-slate-900">{pagination.total}</span> Records
                  </div>
                  <PaginationBar
                    perPageId={perPageSelectId}
                    className="min-w-0 lg:min-w-0 lg:flex-1"
                  />
                </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={listedConfirm.isOpen}
        title={listedConfirm.isCurrentlyActive ? 'Remove Listing' : 'Add Listing'}
        message={
          listedConfirm.isCurrentlyActive
            ? "Removing this listing will prevent potential residents from creating an application for this property on Liberty's site.\n\nAre you sure you want to proceed?"
            : "Listing this property will allow potential residents to create an application for this property on Liberty's site.\n\nAre you sure you want to proceed?"
        }
        messageLayout="listingConfirm"
        primaryLabel="Yes"
        onPrimary={handleConfirmToggleListed}
        secondaryLabel="No"
        onSecondary={() => setListedConfirm((p) => ({ ...p, isOpen: false }))}
        variant="warning"
        overlayDataTestId="property-list-listed-confirm-overlay"
        primaryButtonDataTestId="property-list-listed-confirm-yes-button"
        secondaryButtonDataTestId="property-list-listed-confirm-no-button"
        closeButtonDataTestId="property-list-listed-confirm-close-button"
      />

    </div>
  )
}
